import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { makeStyles } from "@material-ui/core/styles";
import {
  getCSVFileNameString,
  dateFormatterv2,
  dateFormatterv3,
} from "../../util/customFunctions";
import * as formatters from "../AgGrid/gridFormatters";

import "./AgGrid.scss";
import { store } from "../../util/store";

const useStyle = makeStyles((theme) => ({
  tableHeader: {
    whiteSpace: "normal",
    textAlign: "center",
  },
  tableCell: {
    whiteSpace: "normal",
    cursor: (props) => (props.isClickable ? "pointer" : "default"),
  },
}));

const ReusableAgGrid = (props) => {
  const {
    rowData,
    columnConfig,
    onCellClicked = null,
    isExportable = false,
    header = "export",
    rawColumnDataForExport = [], // This prop provides type info for the export logic
    exportTrigger = 0,
    height = undefined,
  } = props;

  const resizeTimeoutRef = useRef(null);

  const state = store.getState();
  const Currency_Symbol = state.app.user.Currency;
  const Value_Label = state.app.user.Value_Label;

  const gridRef = useRef();

  const isClickable = !!onCellClicked; // If onCellClicked is provided, the rows are clickable
  const classes = useStyle({ isClickable });

  useEffect(() => {
    // This check prevents the export from running when the component first loads.
    if (isExportable && exportTrigger > 0) {
      const gridApi = gridRef.current.api;
      if (!gridApi) return;

      const params = {
        skipHeader: false,
        skipFooters: true,
        allColumns: true,
        onlySelected: false,
        suppressQuotes: true,

        processCellCallback: (cellParams) => {
          const rowIndex = cellParams.column.colId;

          const dateTypes = rawColumnDataForExport
            .filter((f) => f.type === "date")
            .map((f) => f.field);
          const dateTimeTypes = rawColumnDataForExport
            .filter((f) => f.type === "datetime")
            .map((f) => f.field);
          const stringTypes = rawColumnDataForExport
            .filter((f) => f.type === "string")
            .map((f) => f.field);
          const integerTypes = rawColumnDataForExport
            .filter((f) => f.type === "integer")
            .map((f) => f.field);
          const currencyTypes = rawColumnDataForExport
            .filter((f) => f.type === "currency")
            .map((f) => f.field);

          // Handle Opp_ID explicitly
          if (rowIndex === "Opp_ID") {
            return cellParams.value ? `"${cellParams.value}"` : "";
          }
          if (cellParams && dateTypes.includes(rowIndex)) {
            return cellParams.value ? dateFormatterv2(cellParams.value) : null; //apply your timestamp formatter
          } else if (cellParams && stringTypes.includes(rowIndex)) {
            return `"${cellParams.value}"`; //apply your string formatter
          } else if (cellParams && dateTimeTypes.includes(rowIndex)) {
            return cellParams.value ? dateFormatterv3(cellParams.value) : null; //apply your timestamp formatter
          } else return cellParams.value; // no formatting
        },
        fileName: getCSVFileNameString(header),
        columnSeparator: ",",
      };
      gridApi.exportDataAsCsv(params);
    }
  }, [exportTrigger, isExportable, rawColumnDataForExport, header]); // This tells the useEffect to ONLY run when exportTrigger changes.

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      wrapText: true,
      autoHeight: true,
      sortable: true,
      resizable: true,
      filter: true,
      cellClass: classes.tableCell,
      headerClass: classes.tableHeader,
      colResizeDefault: "shift",
    }),
    [classes]
  );

  const columnDefs = useMemo(() => {
    // Start with the static '#' column
    const staticCols = [
      {
        headerName: "#",
        colId: "rowIndex",
        valueGetter: (params) => (params.node ? params.node.rowIndex + 1 : ""),
        sortable: false,
        suppressMovable: true,
        pinned: "left",
        filter: false,
        maxWidth: 50,
        cellClassRules: {
          "cell-right-aligned": () => true,
        },
      },
    ];

    staticCols.push({
      headerName: "Opportunity ID",
      field: "Opp_ID",
      hide: true,
    });

    // Map the raw config data to AG Grid column definitions
    const dynamicCols = columnConfig.map((c) => {
      const isRightAligned = [
        "currency",
        "percent",
        "integer",
        "datetime",
        "date",
      ].includes(c.type);

      const columnDef = {
        headerName: c.label.replace(/acv/gi, Value_Label), // Standardize label replacement
        field: c.field,
        filter: c.type === "currency" ? "agNumberColumnFilter" : true,
        sortingOrder: c.type === "currency" ? ["desc", "asc"] : ["asc", "desc"],
        // This forces AG Grid to convert strings/nulls to numbers before comparing
        comparator: ["currency", "integer", "percent"].includes(c.type)
          ? formatters.numericComparator
          : undefined,
        valueFormatter: (params) => {
          if (c.type === "currency")
            return formatters.currencyFormatter(params, Currency_Symbol);
          if (c.type === "datetime")
            return formatters.dateAndTimeFormatter(params);
          if (c.type === "date") return formatters.dateFormatter(params);
          if (c.type === "percent")
            return formatters.percentageDFormatter(params);
          if (c.field === "createEventType")
            return formatters.newOwpFormatter(params);
          if (c.field === "ML_Final_Probability_Score")
            return formatters.percFormatterv2(params);
          return params.value;
        },
        cellClassRules: {
          "cell-right-aligned": () => isRightAligned,
          "cell-left-aligned": () => !isRightAligned,
        },
        headerClass: classes.tableHeader,
        filterParams:
          c.type === "currency" ? { defaultOption: "greaterThanOrEqual" } : {},
        flex: c.flex ? c.flex : 1,
      };

      if (c.tooltip === "true") {
        columnDef.headerTooltip = c.tooltip_text;
      }

      return columnDef;
    });

    return [...staticCols, ...dynamicCols];
  }, [columnConfig, Currency_Symbol, Value_Label]);

  // This is the improved, debounced version of the function
  const onColumnResized = useCallback((params) => {
    // Clear any existing timer
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    // Set a new timer to run resetRowHeights after 200ms
    resizeTimeoutRef.current = setTimeout(() => {
      // Check if the grid API still exists (component might unmount)
      if (params.api) {
        params.api.resetRowHeights();
      }
    }, 200);
  }, []);

  // This handles showing/hiding columns.
  const onColumnVisible = useCallback((params) => {
    // Clear any existing timer
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    // Set a new timer to run resetRowHeights after 200ms
    resizeTimeoutRef.current = setTimeout(() => {
      // Check if the grid API still exists (component might unmount)
      if (params.api) {
        params.api.resetRowHeights();
      }
    }, 200);
  }, []);

  console.log("AgGrid Rendered", rowData);

  // Define a threshold for when to switch behavior.
  // 15 is a good number for a compact view.
  const AUTO_HEIGHT_THRESHOLD = 15;

  // Determine which mode to use based on the row count.
  const useAutoHeight = rowData.length <= AUTO_HEIGHT_THRESHOLD;

  // Determine the domLayout based on height and useAutoHeight
  const domLayout = height ? "normal" : useAutoHeight ? "autoHeight" : "normal";

  return (
    <div
      className="ag-theme-custom-react"
      style={{
        width: "100%",
        // Use the passed 'height' if it exists, otherwise
        // Use auto-height for small datasets, or a fixed height for larger ones.
        height: height ? height : useAutoHeight ? undefined : "70vh",
      }}
    >
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onCellClicked={onCellClicked}
        onColumnResized={onColumnResized}
        onColumnVisible={onColumnVisible}
        domLayout={domLayout}
        headerHeight={55}
        onSortChanged={(params) =>
          params.api.refreshCells({ columns: ["rowIndex"], force: true })
        }
      />
    </div>
  );
};

export default ReusableAgGrid;